import React from 'react';
import styled from 'styled-components';
import CheckoutButtons from './CheckoutButtons';
import ProductImage from '../common/ProductImage';
import { useCart } from '../../contexts/CartContext';
import { getImageUrl } from '../../utils/apiUtils';

// Format price with currency
const formatPrice = (price, currency = 'USD') => {
  if (price === null || price === undefined) return 'Price not available';
  if (price === 0) return 'Free';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency
  }).format(price);
};

const SellerGroupContainer = styled.div`
  background: ${props => props.theme.colors.card};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.radii.md};
  margin-bottom: 0.75rem;
  overflow: hidden;
  transition: all 0.2s;

  &:hover {
    border-color: ${props => props.theme.colors.primary};
    box-shadow: ${props => props.theme.shadows.glowPrimary};
  }
`;

const SellerHeader = styled.div`
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.accent});
  color: white;
  padding: 0.75rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SellerInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const SellerName = styled.h3`
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
`;

const SellerStats = styled.div`
  font-size: 0.8rem;
  opacity: 0.9;
`;

const SellerActions = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const QuickCheckoutButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 0.4rem 0.8rem;
  border-radius: ${props => props.theme.radii.sm};
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    border-color: rgba(255, 255, 255, 0.5);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const SaveForLaterButton = styled.button`
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 0.4rem 0.8rem;
  border-radius: ${props => props.theme.radii.sm};
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.5);
  }
`;

const ItemsList = styled.div`
  padding: 0.75rem;
`;

const ItemRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 0;
  border-bottom: 1px solid ${props => props.theme.colors.border};

  &:last-child {
    border-bottom: none;
  }
`;


const ItemDetails = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const ItemName = styled.div`
  font-weight: 500;
  color: ${props => props.theme.colors.text};
  font-size: 0.9rem;
`;

const ItemPrice = styled.div`
  font-size: 0.8rem;
  color: ${props => props.theme.colors.textDim};
`;

const ItemQuantity = styled.div`
  font-size: 0.8rem;
  color: ${props => props.theme.colors.textDim};
`;

const ItemTotal = styled.div`
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
`;

const RemoveButton = styled.button`
  background: ${props => props.theme.colors.danger};
  color: ${props => props.theme.colors.text};
  border: 1px solid ${props => props.theme.colors.danger};
  padding: 0.25rem 0.5rem;
  border-radius: ${props => props.theme.radii.sm};
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.25rem;

  &:hover {
    background: ${props => props.theme.colors.danger};
    color: white;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(255, 77, 109, 0.3);
  }

  &:active {
    transform: translateY(0);
  }
`;

const SellerGroup = ({ sellerGroup, onCheckout, onSaveForLater, onRequireLogin }) => {
  const { removeFromCart } = useCart();

  const handleQuickCheckout = () => {
    onCheckout(sellerGroup.items);
  };

  const handleSaveForLater = () => {
    onSaveForLater(sellerGroup.sellerId);
  };

  const handleRemoveItem = (productId, productName) => {
    if (window.confirm(`Are you sure you want to remove "${productName}" from your cart?`)) {
      removeFromCart(productId);
    }
  };

  return (
    <SellerGroupContainer>
      <SellerHeader>
        <SellerInfo>
          <SellerName>{sellerGroup.sellerName}</SellerName>
          <SellerStats>
            {sellerGroup.totalItems} {sellerGroup.totalItems === 1 ? 'item' : 'items'} • ${sellerGroup.totalPrice.toFixed(2)}
          </SellerStats>
        </SellerInfo>
        <SellerActions>
          <SaveForLaterButton onClick={handleSaveForLater} title="Save these items for later">
            💾 Save for Later
          </SaveForLaterButton>
          <QuickCheckoutButton onClick={handleQuickCheckout} title="Checkout with this seller">
            🛒 Quick Checkout
          </QuickCheckoutButton>
        </SellerActions>
      </SellerHeader>
      
      <ItemsList>
        {sellerGroup.items.map((item) => (
          <ItemRow key={item.productId}>
            <ProductImage 
              src={item.image ? getImageUrl(item.productId, item.image._id) : null} 
              alt={item.productName}
              size="40px"
              fallbackIcon="📦"
            />
            <ItemDetails>
              <ItemName>{item.productName}</ItemName>
              <ItemPrice>{formatPrice(item.price, item.currency || 'USD')} each</ItemPrice>
              <ItemQuantity>Qty: {item.quantity}</ItemQuantity>
            </ItemDetails>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.5rem' }}>
              <ItemTotal>{formatPrice(item.price * item.quantity, item.currency || 'USD')}</ItemTotal>
              <RemoveButton 
                onClick={() => handleRemoveItem(item.productId, item.productName)}
                title="Remove item from cart"
                aria-label="Remove item from cart"
              >
                🗑️ Remove
              </RemoveButton>
            </div>
          </ItemRow>
        ))}
      </ItemsList>
      
      <CheckoutButtons
        items={sellerGroup.items}
        onCheckout={handleQuickCheckout}
        onRequireLogin={onRequireLogin}
      />
    </SellerGroupContainer>
  );
};

export default SellerGroup;
